import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDebounceEffect } from 'ahooks';
import { useRefFn } from '@axios-use/react';

import { fetchCommonConfig } from '@/apis/home';

const canUseLocalStorage = typeof localStorage !== undefined;
const canUseSessionStorage = typeof sessionStorage !== undefined;

/** 同意的 隐私协议历史版本 */
export const LOCAL_PRIVACY_POP_VERSION_HISTORY_KEY = 'PRIVACY_POP_VERSION_HISTORY';
const LOCAL_PRIVACY_HIDE_POP_BOOL_KEY = 'PRIVACY_HIDE_POP_BOOL';
const LOCAL_PRIVACY_AGREED_KEY = 'PRIVACY_AGREED';

const SESSION_PRIVACY_POP_VERSION_LAST_KEY = 'PRIVACY_POP_VERSION_LAST';
const SESSION_PRIVACY_POP_STYLE_LAST_KEY = 'PRIVACY_POP_STYLE_LAST';
const SESSION_PRIVACY_HIDE_POP_BOOL_KEY = 'SESSION_PRIVACY_HIDE_POP_BOOL';

const sleep = (ms: number) => {
  return new Promise((resolve) => setTimeout(resolve, ms));
};

export const getLocalPrivacyVersionValue = () => {
  if (canUseLocalStorage) {
    return localStorage.getItem(LOCAL_PRIVACY_POP_VERSION_HISTORY_KEY);
  }
  return null;
};

export const clearPrivayPopCache = () => {
  if (canUseLocalStorage) {
    localStorage.removeItem(LOCAL_PRIVACY_HIDE_POP_BOOL_KEY);
  }
};

export const clearPrivayCache = () => {
  if (canUseLocalStorage) {
    localStorage.removeItem(LOCAL_PRIVACY_POP_VERSION_HISTORY_KEY);
    localStorage.removeItem(LOCAL_PRIVACY_HIDE_POP_BOOL_KEY);
    localStorage.removeItem(LOCAL_PRIVACY_AGREED_KEY);
  }
};

export const setPrivacyAgreed = () => {
  if (canUseLocalStorage) {
    localStorage.setItem(LOCAL_PRIVACY_AGREED_KEY, 'true');
  }
};

export const getPrivacyAgreed = (): boolean => {
  if (canUseLocalStorage) {
    return localStorage.getItem(LOCAL_PRIVACY_AGREED_KEY) === 'true';
  }
  return false;
};

export const clearPrivacySessionCache = () => {
  if (canUseSessionStorage) {
    sessionStorage.removeItem(SESSION_PRIVACY_POP_VERSION_LAST_KEY);
    sessionStorage.removeItem(SESSION_PRIVACY_POP_STYLE_LAST_KEY);
    sessionStorage.removeItem(SESSION_PRIVACY_HIDE_POP_BOOL_KEY);
  }
};

export const clearPrivacySessions = () => {
  if (typeof navigator !== 'undefined') {
    const userAgent = navigator.userAgent;
    if (/android/i.test(userAgent)) {
      clearPrivacySessionCache();
    }
  }
};

export const syncAndAgreePrivacy = async () => {
  if (canUseLocalStorage && canUseSessionStorage) {
    setPrivacyAgreed();
    const _sessionLastVersion = sessionStorage.getItem(SESSION_PRIVACY_POP_VERSION_LAST_KEY);
    localStorage.setItem(LOCAL_PRIVACY_HIDE_POP_BOOL_KEY, 'true');

    if (_sessionLastVersion) {
      localStorage.setItem(LOCAL_PRIVACY_POP_VERSION_HISTORY_KEY, _sessionLastVersion);
      return _sessionLastVersion;
    } else {
      const res = await fetchCommonConfig();
      const _v = res?.config.privacypopVersion || '';
      localStorage.setItem(LOCAL_PRIVACY_POP_VERSION_HISTORY_KEY, _v);
      return _v;
    }
  }
  return undefined;
};

/**
 *
 * @param version privacypopVersion
 * @param style privacypopstyle '1' | '2'
 */
export const canShowPrivacy = async (version?: string, style?: string) => {
  if (canUseLocalStorage && canUseSessionStorage) {
    const _localHistoryVersion = localStorage.getItem(LOCAL_PRIVACY_POP_VERSION_HISTORY_KEY);
    const _localHidePop = localStorage.getItem(LOCAL_PRIVACY_HIDE_POP_BOOL_KEY) === 'true';
    const _sessionLastVersion = sessionStorage.getItem(SESSION_PRIVACY_POP_VERSION_LAST_KEY);

    try {
      await sleep(200);
    } catch (error) {
      console.error('[canShowPrivacy] delay error', error);
    }

    if (_localHistoryVersion) {
      if (version == null && _sessionLastVersion == null) {
        try {
          const res = await fetchCommonConfig();
          const _version = res?.config?.privacypopVersion;
          const _style = res?.config?.privacypopstyle;
          if (_version) {
            sessionStorage.setItem(SESSION_PRIVACY_POP_VERSION_LAST_KEY, _version);
            sessionStorage.setItem(SESSION_PRIVACY_POP_STYLE_LAST_KEY, String(_style));

            if (_version !== _localHistoryVersion && _localHidePop) {
              sessionStorage.setItem(SESSION_PRIVACY_HIDE_POP_BOOL_KEY, 'true');
              localStorage.removeItem(LOCAL_PRIVACY_HIDE_POP_BOOL_KEY);
            }
          }
        } catch (error) {
          console.error('[canShowPrivacy] fetch error', error);
        }
      }
    }

    const _sessionHidePop = sessionStorage.getItem(SESSION_PRIVACY_HIDE_POP_BOOL_KEY) === 'true';
    const _defaultShowBool = !_sessionHidePop && !_localHidePop;

    return _defaultShowBool;
  }
  return true;
};

export const getPrivacypopVersion = async (): Promise<{ version: string; style: string } | undefined> => {
  const _sessionVersion = sessionStorage.getItem(SESSION_PRIVACY_POP_VERSION_LAST_KEY);
  const _sessionStyle = sessionStorage.getItem(SESSION_PRIVACY_POP_STYLE_LAST_KEY);

  if (_sessionVersion && _sessionStyle) {
    return { version: _sessionVersion, style: _sessionStyle };
  }

  try {
    const res = await fetchCommonConfig();
    const _version = res?.config?.privacypopVersion;
    const _style = res?.config?.privacypopstyle;

    if (_version && _style) {
      // [faker feature]
      // 如果没有 local 记录（认为为伪首次使用）忽略远程 privacypopstyle，强制为 1 (授权弹窗样式)
      if (getLocalPrivacyVersionValue() == null) {
        return { version: _version, style: '1' };
      }

      return { version: _version, style: _style };
    }
  } catch (error) {
    console.error('[getPrivacypopVersion] error:', error);
  }

  return undefined;
};

const FILTER_PATHS = ['/privacy/confirm', '/welcome/pending'];

export const usePrivacyVersionCheck = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const firstRender = useRef(true);
  const [loading, setLoading] = useState<boolean>();

  const checkPrivacy = () => {
    console.info('[usePrivacyVersionCheck]: call checkPrivacy');
    if (firstRender.current) {
      setLoading(!FILTER_PATHS.includes(location.pathname));
      firstRender.current = false;
    }
    void canShowPrivacy()
      .then((check) => {
        if (check && !FILTER_PATHS.includes(location.pathname)) {
          console.info('[usePrivacyVersionCheck]: navigate to /privacy/confirm');
          navigate('/privacy/confirm');
        } else {
          setLoading(false);
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };
  const checkPrivacyRef = useRefFn(checkPrivacy);

  useDebounceEffect(
    () => {
      checkPrivacyRef.current();
    },
    [location.pathname, navigate],
    {
      wait: 500,
      leading: false,
    },
  );

  return { loading };
};
